import { useEffect, useRef, useState } from "react";
import { wsUrl } from "../lib/api";

/**
 * Chat panel with two variants:
 *   - default : bordered panel (desktop side rail)
 *   - overlay : translucent chip-style overlay (mobile immersive live room)
 *
 * Host power actions (only rendered when isHost=true):
 *   - Pin a message   → shows above chat for everyone
 *   - Delete message  → soft-deletes and broadcasts a tombstone
 *
 * Real-time frames handled:
 *   - history         : first-load hydration
 *   - message         : new chat entry
 *   - chat_state      : open/closed reason + pinned message
 *   - message_deleted : remove a message from the client cache
 */
export default function ChatPanel({ liveId, liveStatus, userName = "Guest", isHost = false, overlay = false, onMilestonesUpdated }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const [chatState, setChatState] = useState({
    open: liveStatus === "live" || liveStatus === "final_window",
    reason: "",
    pinned: null,
  });
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  // Latest onMilestonesUpdated callback stored in a ref so the WS effect below
  // doesn't reconnect every time the parent re-renders.
  const onMilestonesRef = useRef(onMilestonesUpdated);
  useEffect(() => { onMilestonesRef.current = onMilestonesUpdated; }, [onMilestonesUpdated]);

  // Cohosts (admin session running with ?role=cohost) should ALWAYS be able to
  // read + moderate the chat, regardless of the derived open/closed state.
  const effectiveOpen = isHost ? true : chatState.open;

  useEffect(() => {
    if (!liveId) return;
    const ws = new WebSocket(wsUrl(`/api/ws/chat/${liveId}`));
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "history") setMessages(data.messages || []);
        else if (data.type === "message") setMessages((m) => [...m, data.message]);
        else if (data.type === "message_deleted") {
          setMessages((m) => m.filter((msg) => msg.msg_id !== data.msg_id));
        } else if (data.type === "chat_state") {
          setChatState({
            open: !!data.open,
            status: data.status,
            reason: data.reason || "",
            pinned: data.pinned || null,
          });
        } else if (data.type === "milestones_updated") {
          // Cohost changed the price ladder — forward so LiveRoom can refresh
          // instantly instead of waiting for the 3-second poll.
          if (onMilestonesRef.current) onMilestonesRef.current(data);
        }
      } catch (err) { console.error("chat ws message parse error:", err); }
    };
    return () => ws.close();
  }, [liveId]);

  useEffect(() => {
    setChatState((prev) => ({
      ...prev,
      open: liveStatus === "live" || liveStatus === "final_window",
    }));
  }, [liveStatus]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const canSend = effectiveOpen && connected;

  const send = (e) => {
    e?.preventDefault();
    if (!canSend || !text.trim() || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({
      type: "message",
      text: text.trim(),
      user_name: userName,
      is_host: isHost,
    }));
    setText("");
  };

  const pinMessage = (msg) => {
    if (!isHost || wsRef.current?.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({
      type: "pin",
      msg_id: msg.msg_id,
      text: msg.text,
      user_name: userName,
      is_host: true,
    }));
  };
  const unpin = () => {
    if (!isHost || wsRef.current?.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "unpin", user_name: userName, is_host: true }));
  };
  const deleteMessage = (msg) => {
    if (!isHost || wsRef.current?.readyState !== 1) return;
    if (!window.confirm("Delete this message for everyone?")) return;
    wsRef.current.send(JSON.stringify({
      type: "delete_message",
      msg_id: msg.msg_id,
      user_name: userName,
      is_host: true,
    }));
  };

  const placeholder = effectiveOpen
    ? "Type a question..."
    : (chatState.reason || "Chat opens 5 minutes before the drop.");

  /* ============ OVERLAY VARIANT (mobile immersive) ============ */
  if (overlay) {
    const recent = messages.slice(-6);
    return (
      <div className="flex flex-col" data-testid="chat-panel-overlay">
        {/* Pinned message strip (compact) */}
        {chatState.pinned && (
          <PinnedStrip pinned={chatState.pinned} onUnpin={isHost ? unpin : null} compact />
        )}

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto flex flex-col gap-1.5 px-3 py-2 max-h-[26vh]"
          style={{
            maskImage: "linear-gradient(to bottom, transparent 0%, black 30%, black 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 30%, black 100%)",
          }}
          data-testid="chat-messages-overlay"
        >
          {recent.length === 0 && (
            <div className="brand-tiny text-[#6E6E6E] bg-[#FFFFFF]/85 backdrop-blur px-2 py-1 self-start">
              // no messages yet — be the first to ask
            </div>
          )}
          {recent.map((m) => (
            <div
              key={m.msg_id}
              className={`self-start max-w-[85%] px-2 py-1 backdrop-blur-sm rounded-2xl leading-snug group ${
                m.is_host ? "bg-[#FF6A00]/95 text-[#161616]" : "bg-[#FFFFFF]/90 text-[#161616]"
              }`}
              data-testid="chat-message"
            >
              <span className={`font-mono-ds text-[10px] uppercase tracking-widest mr-1.5 ${m.is_host ? "text-[#161616]/90 font-bold" : "text-[#6E6E6E]"}`}>
                {m.is_host && "★ "}
                {m.user_name}:
              </span>
              <span className="font-mono-ds text-[12px] break-words">{m.text}</span>
              {/* Host / co-host moderation controls — available on every
                  message (including other hosts') so any moderator can pin or
                  delete any content. */}
              {isHost && (
                <HostActions
                  compact
                  onPin={() => pinMessage(m)}
                  onDelete={() => deleteMessage(m)}
                />
              )}
            </div>
          ))}
        </div>

        {!effectiveOpen && (
          <div
            className="bg-[#FFFFFF]/90 backdrop-blur px-3 py-1.5 text-center brand-tiny text-[#FF6A00] border-t border-[#E5E1D8]"
            data-testid="chat-gate-message"
          >
            ★ {chatState.reason || "Chat opens 5 minutes before the drop."}
          </div>
        )}

        <form
          onSubmit={send}
          className="flex bg-[#FFFFFF]/90 backdrop-blur border-t border-[#E5E1D8]"
        >
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            disabled={!canSend}
            className="flex-1 bg-transparent px-3 py-2.5 outline-none font-mono-ds text-[13px] text-[#161616] placeholder:text-[#6E6E6E] disabled:cursor-not-allowed disabled:opacity-60 min-w-0"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={!canSend}
            className="px-4 bg-[#FF6A00] hover:bg-[#FF7B1A] text-[#161616] font-mono-ds uppercase text-[11px] tracking-widest transition-colors disabled:bg-[#E5E1D8] disabled:text-[#6E6E6E] disabled:cursor-not-allowed"
            data-testid="chat-send-btn"
          >
            Send
          </button>
        </form>
      </div>
    );
  }

  /* ============ ORIGINAL PANEL VARIANT (desktop side rail / host console) ============ */
  return (
    <div className="flex flex-col border border-[#E5E1D8] bg-[#FFFFFF] h-full" data-testid="chat-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E1D8]">
        <div className="brand-tiny text-[#6E6E6E]">
          LIVE_CHAT/{liveId?.slice(-6)}
        </div>
        <div className="flex items-center gap-2" data-testid="chat-status">
          <span className={`w-2 h-2 ${effectiveOpen && connected ? "bg-[#7ED957] pulse-dot" : "bg-[#E5E1D8]"}`} />
          <span className="brand-tiny text-[#6E6E6E]" data-testid="chat-status-label">
            {!connected ? "OFFLINE" : effectiveOpen ? "OPEN" : "WAITING FOR HOST"}
          </span>
        </div>
      </div>

      {/* Pinned banner (visible to everyone) */}
      {chatState.pinned && (
        <PinnedStrip pinned={chatState.pinned} onUnpin={isHost ? unpin : null} />
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-2 font-mono-ds text-sm space-y-1" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-[#6E6E6E] text-xs italic">// no messages yet — be the first to ask</div>
        )}
        {messages.map((m) => (
          <div key={m.msg_id} className="leading-tight group flex items-start gap-2" data-testid="chat-message">
            <div className="flex-1 min-w-0 break-words">
              <span className={`mr-2 ${m.is_host ? "text-[#FF6A00] font-bold" : "text-[#6E6E6E]"}`}>
                {m.is_host && "★"}
                {m.user_name}:
              </span>
              <span className="text-[#161616]">{m.text}</span>
            </div>
            {isHost && (
              <HostActions onPin={() => pinMessage(m)} onDelete={() => deleteMessage(m)} />
            )}
          </div>
        ))}
      </div>
      {!effectiveOpen && (
        <div
          className="border-t border-[#E5E1D8] bg-[#FFFFFF] px-4 py-2 text-center brand-tiny text-[#FF6A00]"
          data-testid="chat-gate-message"
        >
          ★ {chatState.reason || "Chat opens 5 minutes before the drop."}
        </div>
      )}
      <form onSubmit={send} className="border-t border-[#E5E1D8] flex">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          disabled={!canSend}
          className="flex-1 bg-transparent px-4 py-3 outline-none font-mono-ds text-sm placeholder:text-[#6E6E6E] disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="chat-input"
        />
        <button
          type="submit"
          disabled={!canSend}
          className="px-5 bg-[#FF6A00] hover:bg-[#FF7B1A] text-[#161616] font-mono-ds uppercase text-xs tracking-widest transition-colors disabled:bg-[#E5E1D8] disabled:text-[#6E6E6E] disabled:cursor-not-allowed"
          data-testid="chat-send-btn"
        >
          Send
        </button>
      </form>
    </div>
  );
}

/* ------------------------- Pinned message strip ------------------------- */
function PinnedStrip({ pinned, onUnpin, compact = false }) {
  return (
    <div
      className={`bg-[#FF6A00] text-[#161616] flex items-start gap-2 ${compact ? "px-3 py-1.5" : "px-4 py-2"} border-y border-[#E5E1D8]`}
      data-testid="pinned-message"
    >
      <span className="font-display text-base leading-none mt-0.5">◆</span>
      <div className="flex-1 min-w-0">
        <div className="brand-tiny opacity-80">Pinned by {pinned.pinned_by || "Host"}</div>
        <div className={`font-mono-ds break-words ${compact ? "text-[12px]" : "text-sm"} leading-snug`} data-testid="pinned-text">
          {pinned.text}
        </div>
      </div>
      {onUnpin && (
        <button
          type="button"
          onClick={onUnpin}
          className="brand-tiny border border-white/50 hover:border-white px-2 py-0.5 whitespace-nowrap"
          data-testid="chat-unpin-btn"
        >
          Unpin
        </button>
      )}
    </div>
  );
}

/* ------------------------- Host action buttons ------------------------- */
function HostActions({ onPin, onDelete, compact = false }) {
  return (
    <span
      className={`inline-flex items-center gap-1 ml-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity ${compact ? "text-[9px]" : "text-[10px]"}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onPin}
        className="font-mono-ds uppercase tracking-widest px-1.5 py-0.5 border border-[#FF6A00] text-[#FF6A00] hover:bg-[#FF6A00] hover:text-[#161616]"
        title="Pin this message"
        data-testid="chat-pin-btn"
      >
        Pin
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="font-mono-ds uppercase tracking-widest px-1.5 py-0.5 border border-[#6E6E6E] text-[#6E6E6E] hover:border-[#161616] hover:text-[#161616]"
        title="Delete this message"
        data-testid="chat-delete-btn"
      >
        Del
      </button>
    </span>
  );
}
